/* System defined core library */
import { CommonModule } from '@angular/common';
import { CUSTOM_ELEMENTS_SCHEMA, ModuleWithProviders, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { AccordionModule } from 'ngx-bootstrap/accordion';
import { PopoverModule } from 'ngx-bootstrap/popover';
import { TypeaheadModule } from 'ngx-bootstrap/typeahead';
import { FilterPipeModule } from 'ngx-filter-pipe';
import { OrderModule } from 'ngx-order-pipe';

/* User defined library/components */
import { ProMapModule } from '../map/maps.module';
import { MapUtilityService } from '../map/services/map-utility.service';
import { SharedModule } from '../shared/modules/shared.module';
import { CultureService } from '../shared/services/culture.service';
import { DateTimeService } from '../shared/services/date-time.service';
import { AppointmentSearchComponent } from './appointment-search/appointment-search.component';
import { VerificationDetailComponent } from './appointment-search/verification-detail/verification-detail.component';
import { CancellationAppointmentComponent } from './cancellation-appointment/cancellation-appointment.component';
import { CancellationComponent } from './cancellation-appointment/cancellation/cancellation.component';
import { CancellationConfirmationComponent } from './cancellation-appointment/confirmation/cancellation-confirmation.component';
import { PaymentCancellationComponent } from './cancellation-appointment/payment/payment-cancellation.component';
import { CancellationVerificationComponent } from './cancellation-appointment/verification/cancellation-verification.component';
import { FindAvailabilityLocationComponent } from './find-availability/find-availability-location/find-availability-location.component';
import { FindAvailabilityProctorComponent } from './find-availability/find-availability-proctor/find-availability-proctor.component';
import { FindAvailabilityTestcenterComponent } from './find-availability/find-availability-testcenter/find-availability-testcenter.component';
import { FindAvailabilityTimezoneComponent } from './find-availability/find-availability-timezone/find-availability-timezone.component';
import { FindAvailabilityComponent } from './find-availability/find-availability.component';
import { PaymentComponent } from './reschedule-appointment/payment/payment.component';
import { ReconfirmationComponent } from './reschedule-appointment/reconfirmation/reconfirmation.component';
import { RescheduleAppointmentComponent } from './reschedule-appointment/reschedule-appointment.component';
import { RescheduleLocationComponent } from './reschedule-appointment/reschedule-location/reschedule-location.component';
import { RescheduleMultidaySessionComponent } from './reschedule-appointment/reschedule-multiday-session/reschedule-multiday-session.component';
import { RescheduleMultidayTestCenterComponent } from './reschedule-appointment/reschedule-multiday-testcenter/multiday-testcenter.component';
import { RescheduleProctorComponent } from './reschedule-appointment/reschedule-proctor/reschedule-proctor.component';
import { RescheduleTestCenterComponent } from './reschedule-appointment/reschedule-testcenter/reschedule-testcenter.component';
import { RescheduleTimezoneComponent } from './reschedule-appointment/reschedule-timezone/reschedule-timezone.component';
import { RescheduleComponent } from './reschedule-appointment/reschedule/reschedule.component';
import { BiometricConsentComponent } from './schedule-appointment/biometric-consent/biometric-consent.component';
import { CandidateProfileComponent } from './schedule-appointment/candidate-profile/candidate-profile.component';
import { CustomAgreementComponent } from './schedule-appointment/custom-agreement/custom-agreement.component';
import { MultidayTestCenterComponent } from './schedule-appointment/multiday-testcenter/multiday-testcenter.component';
import { SchedulePaymentComponent } from './schedule-appointment/payment/payment.component';
import { ScheduleLocationComponent } from './schedule-appointment/schedule-location/schedule-location.component';
import { ScheduleProctorComponent } from './schedule-appointment/schedule-proctor/schedule-proctor.component';
import { ScheduleTimezoneComponent } from './schedule-appointment/schedule-timezone/schedule-timezone.component';
import { SchedulingRoutingModule } from './scheduling-routing.module';
import { SchedulingComponent } from './scheduling.component';
import { AppointmentDetailConfirmationComponent } from './shared/components/appointment-detail-confirmation/appointment-detail-confirmation.component';
import { AppointmentDetailComponent } from './shared/components/appointment-detail/appointment-detail.component';
import { AppointmentFilterComponent } from './shared/components/appointment-filter/appointment-filter.component';
import { AppointmentInfoComponent } from './shared/components/appointment-info/appointment-info.component';
import { AppointmentLookupFormComponent } from './shared/components/appointment-lookup-form/appointment-lookup-form.component';
import { AppointmentLookupComponent } from './shared/components/appointment-lookup/appointment-lookup.component';
import { CandidateComponent } from './shared/components/candidate/candidate.component';
import { ClientSelectorComponent } from './shared/components/client-selector/client-selector.component';
import { EligibilityLookupComponent } from './shared/components/eligibility-lookup/eligibility-lookup.component';
import { EligibilityComponent } from './shared/components/eligibility/eligibility.component';
import { ExamCardComponent } from './shared/components/exam-card/exam-card.component';
import { ExamContextComponent } from './shared/components/exam-context/exam-context.component';
import { ExamFeatureComponent } from './shared/components/exam-feature/exam-feature.component';
import { ExamSelectorComponent } from './shared/components/exam-selector/exam-selector.component';
import { ExamSetSelectorComponent } from './shared/components/exam-set-selector/exam-set-selector.component';
import { InvalidRequestComponent } from './shared/components/invalid-request/invalid-request.component';
import { LocationSelectorComponent } from './shared/components/location-selector/location-selector.component';
import { MultidaySessionDetail } from './shared/components/multiday-session-detail/multiday-session-detail.component';
import { MultidayTestCenterSelectorComponent } from './shared/components/multiday-testcenter-selector/multiday-testcenter-selector.component';
import { PaymentDetailComponent } from './shared/components/payment-detail/payment-detail.component';
import { PaymentVoucherDetailComponent } from './shared/components/payment-voucher-detail/payment-voucher-detail.component';
import { PersonalDetailComponent } from './shared/components/personal-detail/personal-detail.component';
import { PreApprovedCommitComponent } from './shared/components/preapproved-commit/preapproved-commit.component';
import { PreapprovedLookupComponent } from './shared/components/preapproved-lookup/preapproved-lookup.component';
import { PreApprovedSelectorComponent } from './shared/components/preapproved-selector/preapproved-selector.component';
import { ProctorSelectorComponent } from './shared/components/proctor-selector/proctor-selector.component';
import { ProgramSelectorComponent } from './shared/components/program-selector/program-selector.component';
import { RegionSelectorComponent } from './shared/components/region-selector/region-selector.component';
import { SelectedExamDetailComponent } from './shared/components/selected-exam-detail/selected-exam-detail.component';
import { SlotCardDetailComponent } from './shared/components/slot-card-detail/slot-card-detail.component';
import { TestCenterSelecterComponent } from './shared/components/testcenter-selecter/testcenter-selecter.component';
import { TestingAccommodationComponent } from './shared/components/testing-accommodation/testing-accommodation.component';
import { TextReminderComponent } from './shared/components/text-reminder/text-reminder.component';
import { TimezoneSelectorComponent } from './shared/components/timezone-selector/timezone-selector.component';
import { TotalDurationComponent } from './shared/components/total-duration/total-duration.component';
import { EligibilityService } from './shared/services/eligibility/eligibility.service';
import { MessagingApiService } from './shared/services/messaging-api.service';
import { MetricApiService } from './shared/services/metric-api.service';
import { OrchestrationApiService } from './shared/services/orchestration-api.service';
import { PaymentApiService } from './shared/services/payment-api.service';
import { RegistrationApiService } from './shared/services/registration-api.service';
import { SchedulingApiService } from './shared/services/scheduling-api.service';
import { SelectedExamDetailService } from './shared/services/selected-exam-detail.service';
import { SitesService } from './shared/services/sites/sites.service';
import { ProgramInformationModule } from './schedule-appointment/program-information/program-information.module';


@NgModule({
  imports: [
    CommonModule,
    TranslateModule,
    SchedulingRoutingModule,
    FormsModule,
    ReactiveFormsModule,
    SharedModule,
    OrderModule,
    FilterPipeModule,
    TypeaheadModule.forRoot(),
    PopoverModule.forRoot(),
    AccordionModule.forRoot(),
    ReactiveFormsModule,
    ProMapModule,
    ProgramInformationModule
  ],
  providers: [
    SchedulingApiService,
    PaymentApiService,
    OrchestrationApiService,
    RegistrationApiService,
    CultureService,
    DateTimeService,
    MapUtilityService,
    MessagingApiService,
    MetricApiService,
    EligibilityService,
    SitesService
  ],
  declarations: [
    SchedulingComponent,
    FindAvailabilityComponent,
    LocationSelectorComponent,
    SelectedExamDetailComponent,
    ExamContextComponent,
    ProgramSelectorComponent,
    RegionSelectorComponent,
    ClientSelectorComponent,
    AppointmentDetailComponent,
    AppointmentFilterComponent,
    AppointmentLookupComponent,
    AppointmentLookupFormComponent,
    AppointmentInfoComponent,
    AppointmentSearchComponent,
    VerificationDetailComponent,
    PersonalDetailComponent,
    PaymentDetailComponent,
    AppointmentDetailConfirmationComponent,
    CandidateComponent,
    CandidateProfileComponent,
    InvalidRequestComponent,
    RescheduleAppointmentComponent,
    RescheduleComponent,
    RescheduleLocationComponent,
    RescheduleTestCenterComponent,
    PaymentComponent,
    ReconfirmationComponent,
    PaymentVoucherDetailComponent,
    CancellationAppointmentComponent,
    CancellationComponent,
    CancellationVerificationComponent,
    PaymentCancellationComponent,
    CancellationConfirmationComponent,
    TextReminderComponent,
    TotalDurationComponent,
    SlotCardDetailComponent,
    AppointmentLookupComponent,
    TestingAccommodationComponent,
    FindAvailabilityTestcenterComponent,
    FindAvailabilityLocationComponent,
    FindAvailabilityTimezoneComponent,
    FindAvailabilityProctorComponent,
    ScheduleLocationComponent,
    SchedulePaymentComponent,
    BiometricConsentComponent,
    CustomAgreementComponent,
    TestCenterSelecterComponent,
    ExamSelectorComponent,
    PreApprovedCommitComponent,
    PreApprovedSelectorComponent,
    EligibilityComponent,
    ExamFeatureComponent,
    ExamCardComponent,
    ScheduleProctorComponent,
    RescheduleProctorComponent,
    ScheduleTimezoneComponent,
    TimezoneSelectorComponent,
    ProctorSelectorComponent,
    RescheduleTimezoneComponent,
    EligibilityLookupComponent,
    PreapprovedLookupComponent,
    ExamSetSelectorComponent,
    MultidayTestCenterSelectorComponent,
    MultidayTestCenterComponent,
    RescheduleMultidayTestCenterComponent,
    MultidaySessionDetail,
    RescheduleMultidaySessionComponent
  ],
  exports: [
    SchedulingComponent,
    FindAvailabilityComponent,
    LocationSelectorComponent,
    SelectedExamDetailComponent,
    ExamContextComponent,
    ProgramSelectorComponent,
    RegionSelectorComponent,
    ClientSelectorComponent,
    AppointmentDetailComponent,
    AppointmentFilterComponent,
    AppointmentLookupComponent,
    AppointmentLookupFormComponent,
    AppointmentInfoComponent,
    AppointmentSearchComponent,
    VerificationDetailComponent,
    PersonalDetailComponent,
    PaymentDetailComponent,
    AppointmentDetailConfirmationComponent,
    CandidateComponent,
    CandidateProfileComponent,
    InvalidRequestComponent,
    RescheduleAppointmentComponent,
    RescheduleComponent,
    RescheduleLocationComponent,
    RescheduleTestCenterComponent,
    PaymentComponent,
    ReconfirmationComponent,
    CancellationAppointmentComponent,
    CancellationComponent,
    CancellationVerificationComponent,
    PaymentCancellationComponent,
    CancellationConfirmationComponent,
    TextReminderComponent,
    TotalDurationComponent,
    SlotCardDetailComponent,
    AppointmentLookupComponent,
    TestingAccommodationComponent,
    FindAvailabilityTestcenterComponent,
    FindAvailabilityLocationComponent,
    FindAvailabilityTimezoneComponent,
    FindAvailabilityProctorComponent,
    ScheduleLocationComponent,
    SchedulePaymentComponent,
    TestCenterSelecterComponent,
    BiometricConsentComponent,
    CustomAgreementComponent,
    ExamSelectorComponent,
    PreApprovedCommitComponent,
    PreApprovedSelectorComponent,
    EligibilityComponent,
    ExamFeatureComponent,
    ExamCardComponent,
    ScheduleProctorComponent,
    RescheduleProctorComponent,
    ScheduleTimezoneComponent,
    TimezoneSelectorComponent,
    ProctorSelectorComponent,
    RescheduleTimezoneComponent,
    EligibilityLookupComponent,
    PreapprovedLookupComponent,
    ExamSetSelectorComponent,
    MultidayTestCenterComponent,
    MultidayTestCenterSelectorComponent,
    RescheduleMultidayTestCenterComponent,
    MultidaySessionDetail,
    RescheduleMultidaySessionComponent
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class SchedulingModule {

  static forRoot(): ModuleWithProviders<SchedulingModule> {
    return {
      ngModule: SchedulingModule,
      providers: [SelectedExamDetailService],
    };
  }
}
